import { Button, Dialog, DialogContent, DialogTitle, Tabs, Tab } from "@mui/material";
import { useEffect, useState } from "react";
import { saveAs } from "file-saver";
import axios from "axios";
import baseURL from "../../../../configs/api";
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import PleaseWaitPopup from "../../../PleaseWaitPopup";
import useAuth from "../../../../components/hooks/useAuth";
import { useTheme } from "../../../../components/hooks/useTheme";
import CustomFormatDatePicker from "../../../../components/common/CustomFormatDatePicker";
import useGeneral from "../../../../components/hooks/useGeneral";
import Cookies from "js-cookie";
import useTenant from "../../../../components/hooks/useTenant";

type PreviewPopupProps = {
    showPopup: boolean;
    setShowPopup: (value: boolean) => void;
    fileName: string;
    setFileName: (value: string) => void;
    vehicleId: number;
    checklistId?: number;
    checkListName?: string;
    isRequired?: boolean;
    isUploaded?: boolean;
    expirationDate?: string;
    tags?: string;
    description?: string;
    onUploadSuccess?: () => void;
};

export const PreviewPopup = ({
    showPopup,
    setShowPopup,
    fileName,
    setFileName,
    vehicleId,
    checklistId,
    checkListName,
    isRequired,
    isUploaded,
    expirationDate,
    tags,
    description: initialDescription,
    onUploadSuccess
}: PreviewPopupProps) => {
    const tenantId = Cookies.get("tenantId-jj-rental-ui") || "";
    const { auth }: any = useAuth();
    const { handleTenantError }: any = useTenant();
    const { themeStyles, theme } = useTheme();
    const { dateFormat }: any = useGeneral();
    const [isFullScreen, setIsFullScreen] = useState<boolean>(false);
    const [isPdf, setIsPdf] = useState<boolean>(false);
    const [isLoadingThePdf, setIsLoadingPdf] = useState<boolean>(false);
    const [dowloading, setDownloading] = useState<boolean>(false);
    const [pdfUrl, setPdfUrl] = useState<string>("");
    const [tabValue, setTabValue] = useState<number>(0);
    const [pleaseWaitPopup, setPleaseWaitPopup] = useState(false);
    const [expiryDate, setExpiryDate] = useState<string>("");
    const [initialTags, setInitialTags] = useState<string>("");
    const [description, setDescription] = useState<string>("");

    useEffect(() => {
        if (showPopup) {
            setExpiryDate(expirationDate ?? "");
            setInitialTags(tags ?? "");
            setDescription(initialDescription ?? "");
        }
         // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showPopup, expirationDate, tags, initialDescription]);

    useEffect(() => {
        if (fileName.split(".")[1]?.toLowerCase() === "pdf") {
            setIsPdf(true);
            setIsLoadingPdf(true);
            axios
                .get(`${baseURL}vehicleFile/Get?name=${fileName}`, {
                    responseType: "arraybuffer",
                    headers: {
                        'X-Tenant-ID': tenantId,
                    },
                })
                .then((res) => {
                    const pdfData = new Uint8Array(res.data);
                    const encodedArray = Array.from(pdfData);
                    const convertedString = encodedArray.reduce((data: any, byte: any) => data + String.fromCharCode(byte), "");
                    const base64string = btoa(convertedString);
                    setPdfUrl(base64string);
                    setIsLoadingPdf(false);
                    handleTenantError(null);
                })
                .catch((error) => {
                    handleTenantError(error);
                    setIsLoadingPdf(false);
                })
        } else {
            setIsPdf(false);
        }

         // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fileName]);

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        try {
            setPleaseWaitPopup(true);
            const formData = new FormData();
            formData.append("ImageFile", file);

            const res = await axios.post(`${baseURL}vehicleFile/upload`, formData, {
                headers: {
                    'X-Tenant-ID': tenantId,
                    "Content-Type": "multipart/form-data"
                },
                withCredentials: true,
            });

            const newFileName = res?.data || file.name;

            const updatePayload = {
                id: checklistId,
                vehicleId,
                checkListName,
                fileName: newFileName,
                isRequired,
                isUploaded,
                expirationDate: expiryDate,
                tags: initialTags,
                description,
                createdAt: new Date().toISOString(),
                createdBy: auth?.givenname,
                updatedAt: new Date().toISOString(),
                updatedBy: auth?.givenname,
            };

            await axios.put(`${baseURL}vehicleCheckList?userId=${auth?.id}`, updatePayload, {
                headers: {
                     'X-Tenant-ID': tenantId,
                }
            });

            setFileName(newFileName);
            if (onUploadSuccess) onUploadSuccess();
            handleTenantError(null);
        } catch (err) {
           handleTenantError(err);
        } finally {
            setPleaseWaitPopup(false);
        }
    };

    const downloadTheImage = () => {
        setDownloading(true);
        saveAs(`${baseURL}vehicleFile/download?name=${fileName}`);
        setDownloading(false);
    };

    const handleTabChange = (_: any, newValue: number) => {
        setTabValue(newValue);
    };

    const handleMetadataSave = async () => {
        try {
            setPleaseWaitPopup(true);

            const updatePayload = {
                id: checklistId,
                vehicleId,
                checkListName,
                fileName,
                isRequired,
                isUploaded,
                expirationDate: expiryDate,
                tags: initialTags,
                description,
                createdAt: new Date().toISOString(),
                createdBy: auth?.givenname,
                updatedAt: new Date().toISOString(),
                updatedBy: auth?.givenname,
            };

            await axios.put(`${baseURL}vehicleCheckList?userId=${auth?.id}`, updatePayload, {
                headers: {
                    'X-Tenant-ID': tenantId,
                }
            });

            if (onUploadSuccess) onUploadSuccess();
            setShowPopup(false);
            handleTenantError(null);
        } catch (err) {
            handleTenantError(err);
        } finally {
            setPleaseWaitPopup(false);
        }
    };

    return (
        <>
            <Dialog
                open={showPopup}
                maxWidth="md"
                fullScreen={isFullScreen}
                PaperProps={{
                    style: {
                        width: isFullScreen ? "" : "35%",
                        marginTop: isFullScreen ? "" : "61px",
                    },
                }}
            >
                <DialogTitle
                    sx={{ padding: "0px", width: "100%" }}
                    className={`${themeStyles.main.tailwindSectionBg}`}
                >
                    <div
                        style={{
                            height: "50px",
                            display: "flex",
                            flexDirection: "row",
                            justifyContent: "space-between",
                            alignItems: "center",
                            padding: "16px 16px 8px 16px",
                        }}
                    >
                        <p style={{ fontSize: "14px", marginLeft: "5px" }}
                            className={`${themeStyles.main.textHeadingColor}`}>
                            {fileName}
                        </p>
                        <CloseRoundedIcon
                            className={`text-xl mr-4 cursor-pointer ${themeStyles.main.textHeadingColor}`}
                            onClick={() => {
                                setIsFullScreen(false);
                                setShowPopup(false);
                            }}
                        />
                    </div>
                    <hr style={{ marginBlock: "0px" }} />
                    {
                        !isFullScreen && (
                            <Tabs value={tabValue} onChange={handleTabChange} variant="fullWidth">
                                <Tab
                                    label="Preview"
                                    sx={{
                                        color: theme === "dark" ? "#E4E7EC" : "inherit"
                                    }}
                                />

                                <Tab
                                    label="Edit"
                                    sx={{
                                        color: theme === "dark" ? "#E4E7EC" : "inherit"
                                    }}
                                />

                            </Tabs>
                        )}
                    <hr style={{ marginBlock: "0px" }} />
                </DialogTitle>

                <DialogContent sx={{ padding: "0px" }} className={`${themeStyles.main.tailwindSectionBg}`}>
                    {tabValue === 0 || isFullScreen ? (
                        <>
                            {isPdf ? (
                                isLoadingThePdf
                                    ? (
                                        <div
                                            style={{ width: "100%", height: isFullScreen ? "calc(100% - 60px)" : "400px" }}
                                            className={`${themeStyles.main.textSubHeadingColor} flex items-center justify-center`}
                                        >
                                            Loading...
                                        </div>
                                    ) : (
                                        <embed
                                            src={`data:application/pdf;base64,${pdfUrl}`}
                                            type="application/pdf"
                                            style={{ width: "100%", height: isFullScreen ? "calc(100% - 60px)" : "400px" }}
                                        />
                                    )

                            ) : (
                                <img
                                    src={`${baseURL}vehicleFile/Get?name=${fileName}`}
                                    alt="hrfile"
                                    className="aspect-square"
                                    style={{ width: "100%", height: isFullScreen ? "calc(100% - 60px)" : "300px" }}
                                />
                            )}

                            <div
                                style={{
                                    width: "100%",
                                    height: "50px",
                                    display: "flex",
                                    flexDirection: "row",
                                    justifyContent: "space-between",
                                    paddingBlock: "8px",
                                    paddingLeft: "16px",
                                    paddingRight: "16px",
                                }}
                            >
                                <div style={{ width: "50%" }}>
                                    <Button
                                        variant="contained"
                                        sx={{
                                            backgroundColor: "#FFFFFF",
                                            color: "black",
                                            borderRadius: "0",
                                            border: "none",
                                            textTransform: "capitalize",
                                            fontSize: "12px",
                                            "&: hover": { backgroundColor: "#e0dee0" },
                                        }}
                                        onClick={() => setIsFullScreen(!isFullScreen)}
                                    >
                                        {isFullScreen && "Exit "} Full Screen
                                    </Button>
                                </div>
                                <div style={{ width: "50%" }} className="flex justify-end gap-2">
                                    <Button
                                        variant="contained"
                                        component="label"
                                        sx={{
                                            backgroundColor: "#FFA500",
                                            color: "white",
                                            borderRadius: 0,
                                            textTransform: "none",
                                            fontSize: "12px",
                                            whiteSpace: "nowrap",
                                            padding: "4px 10px",
                                            "&:hover": {
                                                backgroundColor: "#ff9800",
                                            },
                                        }}
                                    >
                                        Upload New Version
                                        <input type="file" accept=".png, .jpg, .jpeg, .pdf" hidden onChange={handleFileChange} />
                                    </Button>
                                    {dowloading ? (
                                        <Button
                                            variant="contained"
                                            disabled
                                            sx={{ borderRadius: "0", border: "none", textTransform: "capitalize", fontSize: "12px" }}
                                        >
                                            Download
                                            <div className="animate-spin h-5 w-5 rounded-full border-2 border-t-black ml-2 border-white" />
                                        </Button>
                                    ) : (
                                        <Button
                                            variant="contained"
                                            sx={{
                                                backgroundColor: "#4CAF50",
                                                color: "white",
                                                borderRadius: "0",
                                                border: "none",
                                                textTransform: "capitalize",
                                                fontSize: "12px",
                                                "&: hover": { backgroundColor: "#45A049", color: "white" },
                                            }}
                                            onClick={downloadTheImage}
                                        >
                                            Download
                                        </Button>
                                    )}
                                </div>
                            </div>
                        </>
                    ) : (
                        <div className="p-4 flex flex-col gap-4 min-h-[432px]">
                            <div className="space-y-4">
                                <div>
                                    <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Expiry Date</label>
                                    <CustomFormatDatePicker

                                        value={expiryDate}
                                        onChange={setExpiryDate}
                                        dateFormat={dateFormat}
                                        required
                                    />
                                </div>
                            </div>

                            <div className="space-y-4">
                                <div>
                                    <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Tags</label>
                                    <input
                                        type="text"
                                        className={`w-full px-[8px] py-[8px] border rounded-md ${themeStyles.form.tailwindBorderColor} outline-none focus:border-[#167AC3] text-sm ${themeStyles.form.tailwindInputTextColor} bg-transparent`}
                                        value={initialTags}
                                        onChange={(e) => setInitialTags(e.target.value)}
                                        required
                                    />
                                </div>
                            </div>

                            <div className="w-full">
                                <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Description</label>
                                <textarea
                                    className={`w-full px-[8px] py-[8px]  border rounded-md ${themeStyles.form.tailwindBorderColor} outline-none focus:border-[#167AC3] text-sm ${themeStyles.form.tailwindInputTextColor} bg-transparent`}
                                    rows={6}
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                />
                            </div>

                            <Button
                                variant="contained"
                                sx={{ alignSelf: "flex-end", mt: 2 }}
                                onClick={handleMetadataSave}
                            >
                                Save
                            </Button>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            <PleaseWaitPopup pleaseWaitPopup={pleaseWaitPopup} />
        </>
    );
};
